import React, { useEffect, useState } from "react";
import * as echarts from "echarts";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";
import { bottom } from "@popperjs/core";

const monthLabels = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function CategoryCumulativeChart({ years, selectedGlobalYear }) {
    const history = useHistory();
    const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
    const [chartData, setChartData] = useState({});

    const formatToK = (value) => {
        if (value >= 1000000) return (value / 1000000).toFixed(1) + "M";
        if (value >= 1000) return (value / 1000).toFixed(0) + "k";
        return value;
    };

    const currentMonthIndex =
        selectedYear === new Date().getFullYear()
            ? new Date().getMonth() // 0-based
            : 11; // past year → full year allowed

    // Check login
    useEffect(() => {
        if (!localStorage.getItem("userData")) {
            history.push("/login");
        }
    }, []);

    const storedUserData = localStorage.getItem("userData");
    let groupId = "";
    if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        groupId = userData.groupId;
    }

    useEffect(() => {
        setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
    }, [selectedGlobalYear]);

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            if (!groupId) return;

            try {
                const data = await apiService.getCategoryExpenses(groupId, selectedYear);

                // Extract unique categories
                const categories = [
                    ...new Set(
                        data.map((d) => d.expenseDescType.trim()).filter((v) => v !== "")
                    ),
                ];

                const cumulative = {};

                categories.forEach((cat) => {
                    const monthly = new Array(12).fill(0);

                    let runningTotal = 0;

                    for (let m = 1; m <= 12; m++) {
                        // 🚫 Future months (for current year)
                        if (m - 1 > currentMonthIndex) {
                            monthly[m - 1] = null;
                            continue;
                        }

                        const entries = data.filter(
                            (d) => d.expenseDescType.trim() === cat && parseInt(d.month) === m
                        );

                        const monthTotal = entries.reduce(
                            (sum, e) => sum + e.totalExpenses,
                            0
                        );

                        runningTotal += monthTotal;
                        monthly[m - 1] = runningTotal;
                    }

                    cumulative[cat] = monthly;
                });

                setChartData(cumulative);
            } catch (err) {
                console.error("Error fetching cumulative chart:", err);
            }
        };

        fetchData();
    }, [selectedYear]);

    // Render ECharts
    useEffect(() => {
        const chartDom = document.getElementById("cum-chart");
        if (!chartDom || Object.keys(chartData).length === 0) return;

        const chart = echarts.init(chartDom);

        const series = Object.keys(chartData).map((cat) => {
            const data = chartData[cat];

            return {
                name: cat,
                type: "line",
                smooth: true,
                symbol: "circle",
                symbolSize: 6,
                lineStyle: {
                    width: 1.5,
                },
                data,

                // ⭐ END LABEL LIKE YOUR IMAGE
                labelLayout: {
                    moveOverlap: "shiftY"
                },
                endLabel: {
                    show: true,
                    formatter: (params) =>
                        `{bg|${params.seriesName} (${formatToK(params.value)})}`,
                    rich: {
                        bg: {
                            backgroundColor: "#fff",   // White BG
                            padding: [4, 8],              // Top-Bottom, eft-Right padding
                            borderRadius: 4,              // Optional: rounded corners
                            color: "#000",                // Text color
                            fontWeight: 300,
                            fontSize: 10,
                        },
                    },
                },
                emphasis: {
                    focus: "series"
                },
            };
        });

        const option = {
            backgroundColor: "transparent",

            title: {
                // text: "Category-wise Monthly Expense Trend",
                text: undefined,
                left: "center",
                textStyle: {
                    color: "#222",
                    fontSize: 20,
                    fontWeight: "bold",
                },
            },

            legend: {
                show: true,
                bottom: 0,
                type: "scroll",
                textStyle: { color: "#333" },
            },

            tooltip: {
                trigger: "axis",
                backgroundColor: "rgba(255,255,255,0.95)",
                borderColor: "#ddd",
                borderWidth: 1,
                textStyle: { color: "#000" },
                formatter: function (params) {
                    let text = params[0].axisValue + "<br/>";
                    params.forEach((p) => {
                        text += `${p.marker} ${p.seriesName}: ${formatToK(p.value)}<br/>`;
                    });
                    return text;
                },
            },

            grid: {
                left: "3%",
                right: "10%",
                bottom: "12%",
                top: "3%",
                containLabel: true,
            },

            xAxis: {
                type: "category",
                data: monthLabels,
                axisLine: { lineStyle: { color: "#aaa" } },
                axisLabel: { color: "#444" },
            },

            yAxis: {
                type: "value",
                axisLine: { show: false },
                axisLabel: {
                    color: "#444",
                    formatter: (value) => formatToK(value),
                },
                splitLine: {
                    lineStyle: {
                        color: "#eee",
                        type: "solid",
                    },
                },
            },

            color: [
                "#ffc107",
                "#51291e",
                "#95c623",
                "#6610f2",
                "#ff758f",
                "#20c997",
                "#9d4edd",
                "#f77f00",
                "#ff4d6d",
                "#4c9bfd",
                "#0dcaf0",
            ],

            series,

            animationDuration: 10000,
        };

        chart.setOption(option);

        const resizeHandler = () => chart.resize();
        window.addEventListener("resize", resizeHandler);

        return () => {
            window.removeEventListener("resize", resizeHandler);
            chart.dispose();
        };
    }, [chartData]);

    return (
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 w-full">
            <div className="flex justify-between mb-3">
                <h2 className="text-base font-bold text-gray-800 dark:text-white">
                    Category Expense Trend
                </h2>

                <select
                    className="text-gray-800 dark:text-white rounded-md px-3 py-2 text-sm"
                    style={{ minWidth: "80px", border: "1px solid #ddd" }}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>

            <div style={{ width: "100%", overflowX: "auto" }}>
                <div
                    style={{
                        minWidth: window.innerWidth < 768 ? "600px" : "100%",
                    }}
                >
                    <div id="cum-chart" style={{ width: "100%", height: "400px" }}></div>
                </div>
            </div>
        </div>
    );
}